import { MenuItem, Select, TextField } from "@material-ui/core";
import { PaymentMethodResult, Stripe, StripeElements, StripePaymentElementChangeEvent } from "@stripe/stripe-js";
import _ from "lodash";
import moment from "moment";
import React, { Component, forwardRef } from "react";
import { Trans, withTranslation, WithTranslation } from "react-i18next";
import type StripeTypes from "stripe";
import styled from "styled-components";
import { v4 as uuid } from "uuid";

import { PaymentRequiredError } from "apis/_sdk/types/errors";
import {
    IUserStorageModel,
    IWorkspace,
    IWorkspacePlan,
    IWorkspaceUserExtended,
    IWorkspaceUserInvite,
    LegacyTeamMemberRole,
    WorkspacePlanTier,
    WorkspaceUserRole
} from "common/interfaces";
import { emailRegex } from "common/regex";
import stripeProducts, { RecurringPlanDefinition } from "common/stripeProducts";
import getObjectHash from "common/utils/getObjectHash";
import sanitizeRelativeUrl from "common/utils/sanitizeRelativeUrl";
import { getLegacyRoleFromWorkspaceUserRole, getWorkspaceUserRoleFromLegacyRole } from "common/utils/workspaces/users";
import { Icon } from "js/Components/Icon";
import { isPPTAddin } from "js/config";
import { upgradeWorkspacePlan } from "js/controllers/BillingController";
import {
    createWorkspace,
    deleteWorkspace,
    getWorkspace,
    getWorkspacePlan,
    getWorkspaceUserInvites,
    getWorkspaceUsers,
    inviteWorkspaceUsers,
    setPersistentWorkspaceId,
    updateWorkspace
} from "js/controllers/WorkspaceController";
import { getLogger, LogGroup } from "js/core/logger";

import { handleStripeErrors } from "js/core/services/stripe";
import { preventZoom } from "js/core/utilities/preventZoom";
import { trackActivity } from "js/core/utilities/utilities";
import { auth } from "js/firebase/auth";
import { app } from "js/namespaces";
import AddTeamMembers from "js/react/components/AddTeamMembers";
import BeautifulLogo from "js/react/components/BeautifulLogo";
import { BeautifulDialog, ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { Gap10, Gap20 } from "js/react/components/Gap";
import ProgressBar from "js/react/components/ProgressBar";
import { useUserWorkspaces } from "js/react/hooks/useWorkspace";

import SuccessfulPurchaseDialog from "../../../dialogs/SuccessfulPurchaseDialog";
import { AddressForm, MinimalBillingAddress } from "../../components/AddressForm/AddressForm";
import { PaymentForm } from "../../components/PaymentForm/PaymentForm";
import { PromoCoupon, PromoForm } from "../../components/PromoForm/PromoForm";
import { SubmissionButtons } from "../../components/SubmissionButtons/SubmissionButtons";
import { TermsOfUseAndRefundPolicy } from "../../components/TermsOfUseAndRefundPolicy/TermsOfUseAndRefundPolicy";
import { TeamCheckoutSalesLink } from "./TeamCheckoutSalesLink";
import { TeamCheckoutRedesignedView } from "./TeamCheckoutRedesignedView";
import { getProductDetails } from "../../utils/prices";
import { LoadingState } from "../components/LoadingState";

import withElements from "../../../withElements";
import withStripe from "../../../withStripe";

import {
    BeautifulLogoContainer,
    BodySmall,
    Container,
    ContentContainer,
    FormContainer,
    HeaderContainer,
    Heading,
    IconBox,
    PaymentDetailsContainer,
    ProgressBarContainer,
    ScrollContainer,
    WorkspaceSettingsContainer
} from "../../components/FormStyling";

import "css/billing.scss";
import { getTeamCheckoutFlow, TeamCheckoutSteps, type TeamCheckoutLabelOptions } from "./TeamCheckoutFlow";

const logger = getLogger(LogGroup.BILLING);

const TeamNameContainer = styled.div`
    margin:18px 0 30px;
    width: 100%;
`;

const TeamNameField = styled(TextField) <{ error: boolean }>`
    width: 90%;
    label {
        color: ${props => (props.error ? "red" : "inherit")};
    }
`;

const StyledSelect = styled(Select)`
    width: 100%;

    .MuiSelect-select {
        padding-left: 0px;
        font-weight: 400;
        color: rgb(48, 49, 61);
        font-size: 16px;
    }
`;

const SeatCountText = styled.span`
    font-size: 14px;
    display: block;
    margin-bottom: 15px;
    color: #666;
`;

const BoldText = styled.span`
    font-weight: 900;
`;

const TrialText = styled.span`
    font-size: 14px;
    display: block;
    color: #777;
    margin-top: 15px;
`;

const StyledContentContainer = styled(ContentContainer)`
    max-width: 1200px;
    height: 100%;
    position: relative;

    display: flex;
    justify-content: space-between;
    padding: 0 34px;
    gap: 20px;

    @media (max-width: 768px) {
        flex-direction: column;
        padding: 0 20px;
    }

    @media (min-width: 769px) {
        > :nth-child(1),
        > :nth-child(2) {
            flex: 1 1 0;
            min-width: 0;
        }
    }
`;

const StyledWorkspaceSettingsContainer = styled(WorkspaceSettingsContainer)`
    margin: 0;
    padding: 40px 20px 20px;

    @media (max-width: 768px) {
        padding: 20px 10px;
    }
`;

const StyledPaymentDetailsContainer = styled(PaymentDetailsContainer)`
    margin: 0;
    padding: 40px 20px 20px;

    @media (max-width: 768px) {
        padding: 20px 10px;
    }
`;

const StyledAddTeamMembersContainer = styled(AddTeamMembers)`
    max-height: unset;

    >div {
        min-width: unset;
    }
`;

const StyledBodySmall = styled(BodySmall)`
    margin-bottom: 18px;
`;

const ErrorMessageContainer = styled.div`
    color: red;
    font-size: 12px;
    margin-top: 10px;
`;

const SmallText = styled.span`
    font-size: 12px;
`;

const OverlayContainer = styled.div`
    height: 90vh;
    min-width: max-content;

    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;
    border-radius: 4px;
    background: #FFF;
    z-index: 1000;
    box-shadow: 0px 9px 37px 0px rgba(0, 0, 0, 0.2);
    overflow: hidden;

    @media (max-width: 768px) {
        height: 100vh;
        height: 100dvh;
        width: 100vw;
        min-width: unset;
        border-radius: 0;
        box-shadow: none;
        overflow-x: hidden;
}`;

export class UpgradeFailedError extends Error {
    constructor(originalError: Error) {
        super(originalError.message);
        this.name = "UpgradeFailedError";
        this.stack = originalError.stack;
    }
}

const CheckoutContentContainer = styled.div`
    align-self: stretch;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
`;

const FlowProgressContainer = styled.div`
    width: 100%;
    flex-shrink: 0;
`;

const StyledFormContainer = styled(FormContainer)`
    @media (min-width: 1180px) {
        grid-column: 6 / span 21;
    }
`;

const LoadingStateContainer = styled.div`
    position: absolute;
    background: white;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    z-index: 1000;
`;

interface TeamMember {
    id: string;
    email: string;
    role: LegacyTeamMemberRole;
    isDisabled?: boolean;
}

export interface TeamCheckoutProps {
    useRedesignedView?: boolean;
    defaultBillingInterval?: "year" | "month";
    priceIds?: string[];
    productDisplayName?: string;
    minQuantity?: number;
    onSuccess?: () => void;
    onCancel?: () => void;
    workspaceId?: string;
    forceCreateModeUI?: boolean;
    submitLabel?: string;
    title?: string;
    reactivateSubscription?: boolean;
    labelOptions?: TeamCheckoutLabelOptions;
    skipSuccessDialog?: boolean;
    showInlineProgess?: boolean;
    hasProOrTeamSubscription?: boolean;
    skipInviteUsersDialog?: boolean;
}

interface TeamCheckoutState {
    ready: boolean;
    teamName: string;
    members: TeamMember[];
    billingInterval: "year" | "month";
    prices: {
        year: RecurringPlanDefinition;
        month?: RecurringPlanDefinition;
    };
    promo: StripeTypes.PromotionCode | null;
    productDisplayName: string;
    taxAmountPerSeat: number;
    billingAddress: StripeTypes.Address;
    isBillingAddressValid: boolean;
    isPaymentElementComplete: boolean;
    paymentMethodCreationError: string | null;
    error: string | null;
    workspace: IWorkspace | null;
    workspaceUsers: IWorkspaceUserExtended[];
    workspaceUserInvites: IWorkspaceUserInvite[];
    workspacePlan: IWorkspacePlan;
    isAutoDiscoveredWorkspace: boolean;
    isSubmitting: boolean;
    seatQuantity: number;
    teamCheckoutStep: TeamCheckoutSteps;
}

export const TeamCheckoutContent = withStripe(withElements(withTranslation()(class TeamCheckoutContent extends Component<TeamCheckoutProps & { stripe: Stripe, elements: StripeElements } & WithTranslation, TeamCheckoutState> {
    private _restoreZoom: () => void;

    constructor(props: TeamCheckoutProps & { stripe: Stripe, elements: StripeElements } & WithTranslation) {
        super(props);

        const { defaultBillingInterval, priceIds, productDisplayName, minQuantity } = props;

        const tier = WorkspacePlanTier.TEAM;
        const productDefinition = stripeProducts[tier];
        const recurringPlans = productDefinition.plans.filter(plan => plan.type === "recurring" && plan.isDefault) as RecurringPlanDefinition[];
        const prices = {
            year: recurringPlans.find(plan => plan.interval === "year"),
            month: recurringPlans.find(plan => plan.interval === "month")
        };

        let billingInterval = defaultBillingInterval ?? "year";

        const requestedPriceDefinitions = priceIds?.map(id => recurringPlans.find(plan => plan.planId === id)).filter(Boolean) ?? [];
        requestedPriceDefinitions.forEach(price => {
            if (price.interval === "year") {
                prices.year = price;
            } else {
                prices.month = price;
            }
        });
        if (requestedPriceDefinitions.length === 1 && !defaultBillingInterval) {
            billingInterval = requestedPriceDefinitions[0].interval;
        }

        const currentUser = auth().currentUser;

        const members: TeamMember[] = [
            {
                id: currentUser?.uid,
                email: currentUser?.email,
                role: LegacyTeamMemberRole.OWNER,
                isDisabled: true
            }, {
                id: uuid(),
                email: "",
                role: LegacyTeamMemberRole.LIBRARIAN,
            }
        ];

        const targetQuantity = Math.max(minQuantity ?? 6, 2);
        while (members.length < targetQuantity) {
            members.push({
                id: uuid(),
                email: "",
                role: LegacyTeamMemberRole.MEMBER_LICENSED,
            });
        }

        this.state = {
            ready: false,
            teamName: "",
            members,
            billingInterval,
            prices,
            promo: null,
            productDisplayName: productDisplayName ?? productDefinition.productName,
            taxAmountPerSeat: 0,
            billingAddress: { country: "", postal_code: "", city: "", line1: "", line2: "", state: "" },
            isBillingAddressValid: false,
            isPaymentElementComplete: false,
            paymentMethodCreationError: null,
            error: null,
            workspace: null,
            workspaceUsers: [],
            workspaceUserInvites: [],
            workspacePlan: null,
            isAutoDiscoveredWorkspace: false,
            isSubmitting: false,
            seatQuantity: 1,
            teamCheckoutStep: TeamCheckoutSteps.CHECKOUT_OVERLAY,
        };
    }

    get validMembers() {
        return this.state.members.filter(member => member.email && emailRegex.test(member.email));
    }

    get seatCount() {
        if (this.props.useRedesignedView) {
            return this.state.seatQuantity || 1;
        }
        return this.validMembers.filter(member => member.role !== LegacyTeamMemberRole.MEMBER).length;
    }

    get workspaceUsersToInvite() {
        const { workspaceUsers, workspaceUserInvites } = this.state;

        const currentUser = auth().currentUser;

        return this.validMembers
            .filter(m => m.email !== currentUser?.email)
            .filter(m => !workspaceUsers.some(u => u.email === m.email))
            .filter(m => !workspaceUserInvites.some(i => i.email === m.email))
            .map(m => ({
                email: m.email,
                role: getWorkspaceUserRoleFromLegacyRole(m.role)
            }));
    }

    get user() {
        return app.user as IUserStorageModel;
    }

    get showCreateModeUI() {
        return this.props.forceCreateModeUI ?? !this.props.workspaceId;
    }

    get showUpgradeModeUI() {
        return !this.showCreateModeUI;
    }

    get availableTrialDays() {
        const { reactivateSubscription = false } = this.props;
        const { workspacePlan } = this.state;

        if (workspacePlan && workspacePlan.tier !== WorkspacePlanTier.BASIC) {
            return 0;
        }

        // If this is a reactivation (workspaceId is provided and reactivateSubscription is true),
        // don't apply trial logic - charge immediately
        if (reactivateSubscription) {
            return 0;
        }

        return this.user.availableTrialDays[WorkspacePlanTier.TEAM];
    }

    async componentDidMount() {
        const { minQuantity, workspaceId } = this.props;
        const { billingInterval, prices, productDisplayName } = this.state;

        try {
            if (workspaceId) {
                // Only fetch existing workspace if explicitly provided (upgrade mode)
                const workspace = await getWorkspace(workspaceId);
                await this.fetchAndSetWorkspaceData(workspace);
            }
            this.setState({ ready: true });
        } catch (err) {
            logger.error(err, "[TeamCheckout] failed to fetch workspace data");
        }

        this._restoreZoom = preventZoom();

        const params = new URLSearchParams(window.location.search);
        const testId = params.get("ex");
        const createdProps = {
            billing_term: billingInterval,
            price_id: prices[billingInterval].planId,
            plan_display_name: productDisplayName,
            min_quantity: minQuantity,
            test_id: testId
        };
        trackActivity("Organization", "BillingIntentStart", testId, null, createdProps, { audit: false });
    }

    componentWillUnmount() {
        this._restoreZoom?.();
    }

    componentDidUpdate(prevProps: TeamCheckoutProps, prevState: TeamCheckoutState) {
        if (this.state.error) {
            if (
                getObjectHash(prevState.members) !== getObjectHash(this.state.members) ||
                prevState.teamName !== this.state.teamName
            ) {
                this.setState({ error: null });
            }
        }
    }

    fetchAndSetWorkspaceData = async (workspace: IWorkspace, isAutoDiscovered = false) => {
        const { reactivateSubscription, useRedesignedView } = this.props;
        const [workspaceUsers, workspaceUserInvites, workspacePlan] = await Promise.all([
            getWorkspaceUsers(workspace.id),
            getWorkspaceUserInvites(workspace.id),
            getWorkspacePlan(workspace.id)
        ]);

        const members: TeamMember[] = [];

        workspaceUsers.forEach(user => {
            members.push({
                id: user.uid,
                email: user.email,
                role: getLegacyRoleFromWorkspaceUserRole(user.role),
                isDisabled: true
            });
        });
        workspaceUserInvites.forEach(invite => {
            members.push({
                id: invite.id,
                email: invite.email,
                role: getLegacyRoleFromWorkspaceUserRole(invite.role),
                isDisabled: true
            });
        });

        this.validMembers
            .filter(vm => !members.some(m => m.email === vm.email))
            .forEach(vm => {
                members.push(_.cloneDeep(vm));
            });

        if (this.showCreateModeUI) {
            members.push(..._.cloneDeep(this.state.members).filter(member => !member.email));
        }

        let seatQuantity = this.state.seatQuantity;
        if (useRedesignedView && reactivateSubscription) {
            const usedSeatCount = workspaceUsers.filter(u => u.role !== WorkspaceUserRole.GUEST).length + workspaceUserInvites.filter(i => i.role !== WorkspaceUserRole.GUEST).length;
            seatQuantity = Math.max(1, usedSeatCount);
        }

        return new Promise<void>(resolve => this.setState({
            workspace,
            workspaceUsers,
            workspaceUserInvites,
            workspacePlan,
            teamName: this.showCreateModeUI ? "" : workspace.name,
            members,
            isAutoDiscoveredWorkspace: isAutoDiscovered,
            seatQuantity
        }, () => resolve()));
    }

    handleTeamNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ teamName: event.target.value });
    }

    handleMemberUpdate = (id: string, propKey: keyof TeamMember, propValue: any) => {
        this.setState(prevState => ({
            ...prevState,
            members: _.cloneDeep(prevState.members).map(member => member.id === id ? { ...member, [propKey]: propValue } : { ...member })
        }));
    };

    handleAddMember = () => {
        this.setState(prevState => {
            return {
                ...prevState,
                members: _.cloneDeep(prevState.members).concat({
                    id: uuid(),
                    email: "",
                    role: LegacyTeamMemberRole.MEMBER_LICENSED
                })
            };
        });
    };

    handleRemoveMember = (id: string) => {
        const { minQuantity } = this.props;

        if (minQuantity && this.seatCount <= minQuantity) {
            return;
        }

        this.setState(prevState => ({
            ...prevState,
            members: _.cloneDeep(prevState.members).filter(member => member.id !== id)
        }));
    };

    handleBillingAddressChange = ({ address, isAddressValid }: { address: MinimalBillingAddress, isAddressValid: boolean }) => {
        this.setState({
            billingAddress: { ...address, city: "", line1: "", line2: "", state: "" },
            isBillingAddressValid: isAddressValid
        });
    }

    handlePaymentMethodChange = (paymentMethodChangeEvent: StripePaymentElementChangeEvent) => {
        this.setState({
            paymentMethodCreationError: null,
            isPaymentElementComplete: paymentMethodChangeEvent.complete
        });
    }

    handleVerifyPromoCode = async (promo: StripeTypes.PromotionCode, hasTrial: boolean) => {
        const { t } = this.props;
        const { billingInterval, prices } = this.state;
        const priceId = prices[billingInterval].planId;

        const { productPrice, productName, productId } = getProductDetails(priceId);

        // check restrictions
        if (promo.coupon?.applies_to?.products && !promo.coupon.applies_to.products.includes(productId)) {
            throw new Error(t("This coupon can't be applied to a {{productName}} plan", { productName }));
        }

        if (promo.restrictions?.minimum_amount && productPrice < promo.restrictions.minimum_amount) {
            throw new Error(t("This promo can't be applied to bills of less than ${{amount}}", { amount: promo.restrictions.minimum_amount / 100 }));
        }

        if (promo.restrictions?.minimum_amount && hasTrial) {
            throw new Error(t("This promo can't be applied until after your free trial is over"));
        }

        if (promo.restrictions?.first_time_transaction && !hasTrial) {
            throw new Error(t("This promo is only for new users"));
        }

        this.setState({ promo });
    }

    handleTaxCalculated = (taxAmountPerSeat: number) => {
        this.setState({ taxAmountPerSeat });
    }

    handleSubmitPayment = async () => {
        const { stripe, elements, onSuccess, minQuantity, skipSuccessDialog, showInlineProgess } = this.props;
        const { t } = this.props;
        let { billingInterval, prices, teamName, productDisplayName, billingAddress, promo, workspace: existingWorkspace } = this.state;

        teamName = teamName.trim();

        if (!teamName) {
            this.setState({ error: t("Company name is required") });
            return;
        }

        this.setState({ isSubmitting: true });

        let progressDialog = null;
        const showProgress = () => {
            this.setState({ isSubmitting: true });

            if (!showInlineProgess) {
                progressDialog = ShowDialog(ProgressDialog, {
                    title: t("Creating your workspace...")
                });
            }
        };

        const setProgressMessage = (message: string) => {
            progressDialog?.setTitle(message);
        };

        const closeProgress = () => {
            this.setState({ isSubmitting: false });
            progressDialog?.close();
        };

        showProgress();

        let paymentMethod: StripeTypes.PaymentMethod | null = null;
        try {
            const { error } = await elements.submit();
            if (error) {
                this.setState({ paymentMethodCreationError: error.message, isSubmitting: false });
                closeProgress();
                return;
            }

            const { paymentMethod: createdPaymentMethod } = await handleStripeErrors<PaymentMethodResult>(() => stripe.createPaymentMethod({
                elements,
                params: {
                    billing_details: {
                        address: {
                            country: billingAddress.country,
                            postal_code: billingAddress.postal_code
                        }
                    }
                }
            }));

            paymentMethod = createdPaymentMethod as unknown as StripeTypes.PaymentMethod;
        } catch (error) {
            logger.error(error, "Failed to create payment method");
            this.setState({ paymentMethodCreationError: error.message, isSubmitting: false });
            closeProgress();
            return;
        }

        try {
            let workspaceId = existingWorkspace?.id;
            if (!workspaceId) {
                workspaceId = (await createWorkspace({ name: teamName })).id;
            }

            const availableTrialDays = this.availableTrialDays;
            const priceId = prices[billingInterval].planId;
            const hasTrial = availableTrialDays > 0;

            setProgressMessage(
                this.showCreateModeUI
                    ? (hasTrial ? t("Workspace created! Starting free trial...") : t("Workspace created! Purchasing seats..."))
                    : (hasTrial ? t("Upgrading workspace and starting free trial...") : t("Upgrading workspace and purchasing seats..."))
            );

            try {
                await upgradeWorkspacePlan({
                    workspaceId,
                    quantity: this.seatCount,
                    tier: WorkspacePlanTier.TEAM,
                    priceId: priceId,
                    paymentMethodId: paymentMethod.id,
                    billingDetails: {
                        address: billingAddress
                    },
                    promotionCode: promo?.code,
                    analyticsProps: {
                        seat_recipients: this.validMembers.map(m => m.email),
                        min_quantity: minQuantity
                    }
                });
            } catch (err) {
                if (!existingWorkspace) {
                    await deleteWorkspace(workspaceId);
                }

                throw err;
            }

            if (existingWorkspace) {
                await updateWorkspace(workspaceId, { name: teamName });
            }

            if (this.workspaceUsersToInvite.length > 0) {
                setProgressMessage(hasTrial
                    ? t("Trial started! Inviting your team members...")
                    : t("Seats purchased! Inviting your team members..."));
                await inviteWorkspaceUsers(workspaceId, this.workspaceUsersToInvite);
            }

            setPersistentWorkspaceId(workspaceId, "CREATED");

            if (skipSuccessDialog) {
                onSuccess?.();
                return;
            }

            const dialog = ShowDialog(SuccessfulPurchaseDialog, {
                productDisplayName,
                callToActionText: t("Continue"),
                showTrial: hasTrial,
                trialExpirationDate: moment().add(availableTrialDays, "days").format("MMMM Do YYYY"),
                onCallToActionClick: () => {
                    dialog.close();

                    if (!isPPTAddin) {
                        const continueUrl = sanitizeRelativeUrl(new URLSearchParams(window.location.search).get("continue") ?? "/");

                        // If the continue URL is the same as the current URL, we need to force a reload in order to update the user's subscription status.
                        if (continueUrl !== window.location.pathname) {
                            window.location.href = continueUrl;
                        } else {
                            window.location.reload();
                        }
                    }

                    onSuccess?.();
                }
            });

            // Legacy
            trackActivity("SignupFlow", "NavForward", null, null, {
                step_number: 8,
                step_name: "Checkout",
                plan_selected: "team",
                experiment_group_assignment: "B",
                object: "button",
                object_label: !hasTrial ? "Confirm" : "Start Free Trial",
                action: "clicked",
                workspace_id: workspaceId
            }, { audit: true });
            //////
        } catch (err) {
            logger.error(err, "[TeamCheckout] handleSubmit() failed");

            if (err instanceof PaymentRequiredError) {
                ShowErrorDialog({
                    title: t("There was a problem processing your payment"),
                    message: (<>
                        {t("Sorry, we had a problem processing your payment")}
                        <br />
                        <SmallText>{t("Error: {{message}}", { message: err.message })}</SmallText>
                    </>)
                });
            } else {
                ShowErrorDialog({
                    title: this.showCreateModeUI ? t("Error creating workspace") : t("Error upgrading workspace"),
                    message: (<>
                        {t("Sorry, we had a problem {{action}} your workspace", { action: this.showCreateModeUI ? t("creating") : t("upgrading") })}
                        <br />
                        <SmallText>{t("Error: {{message}}", { message: err.message })}</SmallText>
                    </>)
                });
            }
        } finally {
            closeProgress();
        }
    }

    // Submit payment for the team checkout
    // This is a new handleSubmitPayment function that is used in the TeamCheckoutRedesignedView component
    handleSubmitPaymentRedesigned = async () => {
        const { t } = this.props;
        const { stripe, elements, minQuantity, skipInviteUsersDialog, onSuccess } = this.props;
        let { billingInterval, prices, billingAddress, promo, workspace: existingWorkspace } = this.state;

        const availableTrialDays = this.availableTrialDays;
        const hasTrial = availableTrialDays > 0;

        this.setState({ isSubmitting: true });

        const showProgress = () => {
            this.setState({ isSubmitting: true });
        };

        const closeProgress = () => {
            this.setState({ isSubmitting: false });
        };

        showProgress();

        let paymentMethod: StripeTypes.PaymentMethod | null = null;
        try {
            const { error } = await elements.submit();
            if (error) {
                this.setState({ paymentMethodCreationError: error.message, isSubmitting: false });
                closeProgress();
                return;
            }

            const { paymentMethod: createdPaymentMethod } = await handleStripeErrors<PaymentMethodResult>(() => stripe.createPaymentMethod({
                elements,
                params: {
                    billing_details: {
                        address: {
                            country: billingAddress.country,
                            postal_code: billingAddress.postal_code
                        }
                    }
                }
            }));

            paymentMethod = createdPaymentMethod as unknown as StripeTypes.PaymentMethod;
        } catch (error) {
            logger.error(error, "Failed to create payment method");
            this.setState({ paymentMethodCreationError: error.message, isSubmitting: false });
            closeProgress();
            return;
        }

        try {
            let workspace = existingWorkspace;

            if (!workspace?.id) {
                workspace = (await createWorkspace({ name: `${auth().currentUser?.email?.split("@")[0] ?? "User"}'s team workspace` }));
            }

            const priceId = prices[billingInterval].planId;

            try {
                await upgradeWorkspacePlan({
                    workspaceId: workspace.id,
                    quantity: this.seatCount,
                    tier: WorkspacePlanTier.TEAM,
                    priceId: priceId,
                    paymentMethodId: paymentMethod.id,
                    billingDetails: {
                        address: billingAddress
                    },
                    promotionCode: promo?.code,
                    analyticsProps: {
                        seat_recipients: this.validMembers.map(m => m.email),
                        min_quantity: minQuantity
                    }
                });
            } catch (err) {
                if (!existingWorkspace) {
                    await deleteWorkspace(workspace.id);
                }

                throw err;
            }

            setPersistentWorkspaceId(workspace.id, "CREATED");

            trackActivity("SignupFlow", "NavForward", null, null, {
                step_number: 8,
                step_name: "Checkout",
                plan_selected: "team",
                object_label: !hasTrial ? "Confirm" : "Start Free Trial",
                action: "clicked",
                workspace_id: workspace.id
            }, { audit: true });

            if (skipInviteUsersDialog) {
                onSuccess?.();
                return;
            }

            this.setState({ teamCheckoutStep: TeamCheckoutSteps.INVITE_USERS,
                workspace: workspace, isSubmitting: false });
        } catch (err) {
            logger.error(err, "[TeamCheckout] handleSubmitRedesigned() failed");

            if (err instanceof PaymentRequiredError) {
                ShowErrorDialog({
                    title: t("Error Processing Payment"),
                    message: (<>
                        {t("An error occurred while processing your payment. {{message}}", { message: err.message })}
                    </>)
                });
            } else {
                ShowErrorDialog({
                    title: this.showCreateModeUI ? t("Error creating workspace") : t("Error upgrading workspace"),
                    message: (<>
                        {t("Sorry, we had a problem {{action}} your workspace", { action: this.showCreateModeUI ? t("creating") : t("upgrading") })}
                        <br />
                        <SmallText>{t("Error: {{message}}", { message: err.message })}</SmallText>
                    </>)
                });
            }
        } finally {
            closeProgress();
        }
    }

    handleCancel = () => {
        if (this.props.onCancel) {
            this.props.onCancel();
        }
    }

    handleSeatQuantityChange = (seatQuantity: number) => {
        const nextSeatQuantity = Math.max(1, seatQuantity);
        this.setState({ seatQuantity: nextSeatQuantity });
    };

    render() {
        const {
            submitLabel: defaultSubmitLabel,
            title: defaultTitle,
            showInlineProgess,
            onSuccess,
            hasProOrTeamSubscription,
            useRedesignedView,
            reactivateSubscription,
            labelOptions,
            onCancel
        } = this.props;
        const { t } = this.props;

        const {
            billingInterval,
            members,
            prices,
            taxAmountPerSeat,
            promo,
            teamName,
            error,
            productDisplayName,
            billingAddress,
            isBillingAddressValid,
            isPaymentElementComplete,
            paymentMethodCreationError,
            workspace,
            ready,
            isSubmitting,
            teamCheckoutStep
        } = this.state;

        if (!ready) {
            return <StyledContentContainer><FetchingClickShield visible={true} backgroundColor="white" /></StyledContentContainer>;
        }

        if (useRedesignedView) {
            const canSubmit = isBillingAddressValid && isPaymentElementComplete && taxAmountPerSeat !== null;
            const checkoutFlow = getTeamCheckoutFlow(reactivateSubscription ?? false, hasProOrTeamSubscription && !reactivateSubscription);

            return (
                <TeamCheckoutRedesignedView
                    onCancel={onCancel}
                    checkoutFlow={checkoutFlow}
                    labelOptions={labelOptions}
                    error={error}
                    onSuccess={onSuccess}
                    billingInterval={billingInterval}
                    prices={prices}
                    promo={promo}
                    workspaceId={workspace?.id}
                    seatCount={this.seatCount}
                    availableTrialDays={this.availableTrialDays}
                    billingAddress={billingAddress}
                    paymentMethodCreationError={paymentMethodCreationError}
                    isSubmitting={isSubmitting}
                    canSubmit={canSubmit}
                    onBillingIntervalChange={interval => this.setState({ billingInterval: interval })}
                    onSeatCountChange={this.handleSeatQuantityChange}
                    onPaymentElementChange={this.handlePaymentMethodChange}
                    onBillingAddressChange={this.handleBillingAddressChange}
                    onVerifyPromoCode={promoCode => this.handleVerifyPromoCode(promoCode, this.availableTrialDays > 0)}
                    onSubmit={this.handleSubmitPaymentRedesigned}
                    teamCheckoutStep={teamCheckoutStep}
                    taxAmountPerSeat={taxAmountPerSeat}
                />
            );
        }

        const planDefinition = prices[billingInterval];

        let amount = planDefinition.amount / 100;
        let adjustedTaxPerSeat = taxAmountPerSeat / 100;
        if (promo?.coupon) {
            const amountBefore = amount;
            if (promo.coupon.amount_off) {
                amount = Math.max(amount - (promo.coupon.amount_off / 100), 0);
            } else {
                amount = amount - (amount / 100 * promo.coupon.percent_off);
            }
            const ratio = amount / amountBefore;
            adjustedTaxPerSeat = adjustedTaxPerSeat * ratio;
        }

        const seatCount = this.seatCount;

        const pricePerSeatWithTax = amount.toFixed(2) + (adjustedTaxPerSeat ? ` ${t("plus tax")}` : "");
        const totalPriceWithTax = (amount * seatCount + adjustedTaxPerSeat * seatCount).toFixed(2);

        const canSubmit =
            isBillingAddressValid &&
            isPaymentElementComplete &&
            taxAmountPerSeat !== null &&
            !members.some(member => member.email.length > 0 && !emailRegex.test(member.email)) &&
            !members.some(member => members.some(otherMember => member.email && member.email.toLowerCase() === otherMember.email.toLowerCase() && member.id !== otherMember.id));

        const hasTrial = this.availableTrialDays > 0;
        const trialExpirationDate = moment().add(this.availableTrialDays, "days");

        const submitLabel = defaultSubmitLabel ?? (hasTrial
            ? t("Start Free Trial")
            : this.showCreateModeUI ? t("Confirm") : t("Upgrade"));

        const title = defaultTitle ?? (
            this.showCreateModeUI
                ? t("Create a Team")
                : t("Upgrade \"{{workspaceName}}\" workspace to Team plan", { workspaceName: workspace.name })
        );

        return (
            <StyledContentContainer>
                {showInlineProgess && isSubmitting &&
                    <LoadingStateContainer>
                        <LoadingState title={hasTrial ? t("Starting free trial") : t("Upgrading plan")} subtitle={t("This will only take a moment...")} />
                    </LoadingStateContainer>
                }
                <StyledWorkspaceSettingsContainer>
                    <Heading>
                        <IconBox>
                            <Icon blue>domain</Icon>
                        </IconBox>
                        {title}
                    </Heading>
                    <TeamNameContainer>
                        <TeamNameField
                            autoFocus
                            onChange={this.handleTeamNameChange}
                            value={teamName}
                            variant="standard"
                            name={t("Company Name")}
                            label={t("Company Name")}
                            disabled={this.showUpgradeModeUI}
                            error={error && !teamName}
                        />
                    </TeamNameContainer>
                    <StyledAddTeamMembersContainer
                        members={members}
                        onUpdateMember={this.handleMemberUpdate}
                        onAddMember={this.handleAddMember}
                        onRemoveMember={this.handleRemoveMember}
                        availableRoles={Object.values(LegacyTeamMemberRole).filter(role => role !== LegacyTeamMemberRole.MEMBER)}
                    />
                </StyledWorkspaceSettingsContainer>
                <StyledPaymentDetailsContainer>
                    {prices.month && (<>
                        <StyledBodySmall>{t("Choose plan")}</StyledBodySmall>
                        <StyledSelect
                            SelectDisplayProps={{ style: { textTransform: "capitalize" } }}
                            value={billingInterval}
                            inputProps={{ className: "select-input" }}
                            onChange={event => {
                                this.setState({ billingInterval: event.target.value as "year" | "month" });
                            }}
                            renderValue={value => {
                                if (value === "year") {
                                    return t("Annual Plan - ${{price}} /month", { price: prices.year.amount / 12 / 100 });
                                }
                                return t("Monthly Plan - ${{price}}/month", { price: prices.month?.amount / 100 });
                            }}
                        >
                            <MenuItem value="year">
                                {t("Annual Plan - ${{price}}/month per Team Pro Seat", { price: prices.year.amount / 12 / 100 })}
                            </MenuItem>
                            <MenuItem value="month">
                                {t("Monthly Plan - ${{price}}/month per Team Pro Seat", { price: prices.month?.amount / 100 })}
                            </MenuItem>
                        </StyledSelect>
                    </>)}
                    {!prices.month &&
                        <b>{t("{{productName}} - Annual Plan", { productName: productDisplayName })}</b>
                    }
                    <Gap10 />
                    <div>
                        <SeatCountText>
                            <Trans
                                t={t}
                                i18nKey="You are adding a total of {{count}} Pro team {{seatLabel}}, including your own, at <price>{{price}}</price> per seat."
                                values={{
                                    count: seatCount,
                                    seatLabel: seatCount > 1 ? t("seats") : t("seat"),
                                    price: `$${pricePerSeatWithTax}/${billingInterval}`
                                }}
                                components={{ price: <BoldText /> }}
                            />
                        </SeatCountText>
                    </div>
                    <Gap10 />
                    <PaymentForm
                        paymentMethodCreationError={paymentMethodCreationError}
                        handlePaymentElementChange={this.handlePaymentMethodChange}
                    />
                    <Gap20 />
                    <AddressForm
                        initialAddress={billingAddress}
                        onChange={this.handleBillingAddressChange}
                    />
                    <Gap20 />
                    {!promo ? <PromoForm verifyPromoCode={promoCode => this.handleVerifyPromoCode(promoCode, hasTrial)} /> : <PromoCoupon coupon={promo.coupon} />}
                    <Gap20 />
                    <SubmissionButtons
                        submitLabel={submitLabel}
                        onSubmit={this.handleSubmitPayment}
                        onCancel={this.showUpgradeModeUI ? this.handleCancel : undefined}
                        disabled={!canSubmit}
                    />
                    {error &&
                        <ErrorMessageContainer>
                            {error}
                        </ErrorMessageContainer>
                    }
                    <TrialText>
                        {hasTrial && <>
                            <Trans
                                t={t}
                                i18nKey="Once your 14 day free trial ends on <bold>{{date}}</bold>, you will be charged <bold>${{total}}</bold>."
                                values={{
                                    date: trialExpirationDate.format("MMMM D, YYYY"),
                                    total: totalPriceWithTax
                                }}
                                components={{ bold: <BoldText /> }}
                            />
                        </>}
                        {!hasTrial && <>
                            <Trans
                                t={t}
                                i18nKey="You will be charged <bold>${{total}}</bold>."
                                values={{ total: totalPriceWithTax }}
                                components={{ bold: <BoldText /> }}
                            />
                        </>}
                        {" "}
                        <Trans
                            t={t}
                            i18nKey="Your subscription will <bold>automatically renew {{interval}}</bold>."
                            values={{ interval: billingInterval == "year" ? t("annually") : t("monthly") }}
                            components={{ bold: <BoldText /> }}
                        />
                        {" "}
                        {t("Update your billing information, add and remove seats or cancel your subscription at any time from your account management.")}{" "}
                        <TermsOfUseAndRefundPolicy />
                    </TrialText>
                    {hasTrial &&
                        <TeamCheckoutSalesLink t={t} />
                    }
                </StyledPaymentDetailsContainer>
            </StyledContentContainer>
        );
    }
}), { appearanceVariant: "A" }));

export default function TeamCheckoutContentWithContainer(props: TeamCheckoutProps) {
    const { workspaces: userWorkspaces, loading: isLoadingUserWorkspaces } = useUserWorkspaces();

    if (isLoadingUserWorkspaces) {
        return <FetchingClickShield visible backgroundColor="white" />;
    }

    const hasProOrTeamSubscription = userWorkspaces.some(metadata =>
        metadata.plan.tier === WorkspacePlanTier.PRO || metadata.plan.tier === WorkspacePlanTier.TEAM
    );

    const content = <TeamCheckoutContent hasProOrTeamSubscription={hasProOrTeamSubscription} {...props} />;

    if (props.useRedesignedView) {
        return (
            <OverlayContainer style={{ height: "auto" }}>
                <CheckoutContentContainer>
                    {content}
                </CheckoutContentContainer>
            </OverlayContainer>
        );
    }

    return (
        <Container>
            <HeaderContainer>
                <BeautifulLogoContainer>
                    <BeautifulLogo forceMarketing width={132.855} />
                </BeautifulLogoContainer>
                <ProgressBarContainer>
                    <ProgressBar progress={100} prevProgress={80} />
                </ProgressBarContainer>
            </HeaderContainer>
            <ScrollContainer>
                <ContentContainer>
                    <StyledFormContainer>
                        {content}
                    </StyledFormContainer>
                </ContentContainer>
            </ScrollContainer>
        </Container>
    );
}

export const TeamCheckoutDialog = forwardRef(
    function TeamCheckoutDialog({ closeDialog, maxWidth, ...props }: { closeDialog: (upgraded?: boolean) => void, maxWidth?: "sm" | "md" | "lg" | "xl" } & TeamCheckoutProps, ref: React.Ref<any>) {
        return (
            <BeautifulDialog
                preventClose={true}
                fullWidth
                maxWidth={maxWidth ?? "lg"}
                PaperProps={{ style: { backgroundColor: "white", margin: 0 } }}
                ref={ref}
            >
                <TeamCheckoutContent
                    ref={ref}
                    {...props}
                    onSuccess={() => closeDialog(true)}
                    onCancel={() => closeDialog(false)}
                />
            </BeautifulDialog>
        );
    }
);
